import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Pagination,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Slideshow as SlideshowIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Info as InfoIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useViewerData } from './viewerHttp';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '90vw',
    height: '90vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const PowerPointContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  backgroundColor: '#f8f9fa',
  overflow: 'hidden',
}));

const SlidesPanel = styled(Box)(({ theme }) => ({
  width: '280px',
  backgroundColor: 'white',
  borderRight: '1px solid rgba(87, 87, 176, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const SlidesPanelHeader = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  backgroundColor: '#f8f9fa',
}));

const SlidesList = styled(List)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: 0,
  '& .MuiListItem-root': {
    borderBottom: '1px solid #f0f0f0',
    '&:hover': {
      backgroundColor: 'rgba(87, 87, 176, 0.05)',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(87, 87, 176, 0.1)',
      borderLeft: `4px solid ${primaryColor}`,
    },
  },
}));

const MainContent = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const SlideViewer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  backgroundColor: '#f8f9fa',
  overflow: 'auto',
}));

const SlideCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: '800px',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '500px',
  display: 'flex',
  flexDirection: 'column',
}));

const SlideContent = styled(CardContent)(({ theme }) => ({
  flex: 1,
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontWeight: 600,
    marginBottom: '16px',
    color: '#1a202c',
  },
  '& h1': {
    fontSize: '2.5em',
    textAlign: 'center',
  },
  '& h2': {
    fontSize: '2em',
  },
  '& h3': {
    fontSize: '1.5em',
  },
  '& p': {
    marginBottom: '16px',
    fontSize: '1.1em',
    lineHeight: '1.6',
  },
  '& ul, & ol': {
    marginBottom: '16px',
    paddingLeft: '32px',
  },
  '& li': {
    marginBottom: '8px',
    fontSize: '1.1em',
    lineHeight: '1.6',
  },
  '& .slide-title': {
    fontSize: '2.5em',
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: '24px',
    color: primaryColor,
  },
  '& .slide-subtitle': {
    fontSize: '1.5em',
    textAlign: 'center',
    marginBottom: '32px',
    color: '#666',
  },
  '& .slide-content': {
    fontSize: '1.2em',
    lineHeight: '1.6',
  },
}));

const SlideNavigation = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  backgroundColor: 'white',
  borderTop: '1px solid rgba(87, 87, 176, 0.1)',
}));

const InfoPanel = styled(Box)(({ theme }) => ({
  padding: '16px',
  backgroundColor: '#f8f9fa',
  borderTop: '1px solid rgba(87, 87, 176, 0.1)',
}));

const PowerPointViewer = ({ open, onClose, filename = '', eventId = null, filePath = '' }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data: pptData,
    loading,
    error,
  } = useViewerData({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.powerpoint_missing_source', 'File id or path is required'),
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setCurrentSlide(0);
    }
  }, [open, eventId, filePath]);

  const downloadPpt = () => {
    if (pptData) {
      // Create a formatted text export
      const content = [
        `PowerPoint Presentation: ${filename}`,
        `Title: ${pptData.title || 'Untitled'}`,
        `Author: ${pptData.author || 'Unknown'}`,
        `Created: ${pptData.created_date || 'Unknown'}`,
        `Slides: ${pptData.slides?.length || 0}`,
        '',
        '--- Slides ---',
        '',
        ...(pptData.slides || []).map((slide, index) => [
          `Slide ${index + 1}: ${slide.title || 'Untitled'}`,
          slide.content || 'No content',
          ''
        ].join('\n'))
      ].join('\n');
      
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename || 'presentation'}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Navigate slides
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    if (pptData && currentSlide < pptData.slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // Filter slides based on search term
  const filteredSlides = pptData?.slides?.filter(slide =>
    slide.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    slide.content?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return dateString;
    }
  };

  // Get current slide or fallback
  const getCurrentSlide = () => {
    if (!pptData || !pptData.slides || pptData.slides.length === 0) return null;
    return pptData.slides[currentSlide] || pptData.slides[0];
  };

  const currentSlideData = getCurrentSlide();

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <SlideshowIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.powerpoint_viewer_title')} - ${filename}` : t('viewer.powerpoint_viewer_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadPpt} 
            disabled={!pptData}
            size="small"
            title={t('viewer.download_powerpoint')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
        
        {pptData && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={`${pptData.slides?.length || 0} slides`}
              size="small"
              variant="outlined"
            />
            <Chip 
              label={`Slide ${currentSlide + 1}`}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
      </ToolbarContainer>

      <DialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'hidden' }}>
        <PowerPointContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              gap: 2
            }}>
              <LinearProgress sx={{ width: 300 }} />
              <Typography color="text.secondary">
                {t('viewer.loading_powerpoint')}
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              padding: 4
            }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_powerpoint')}
              </Typography>
              <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                {error}
              </Typography>
            </Box>
          ) : pptData ? (
            <>
              {/* Slides Panel */}
              <SlidesPanel>
                <SlidesPanelHeader>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('viewer.search_slides_placeholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={clearSearch}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </SlidesPanelHeader>
                
                <SlidesList>
                  {(searchTerm ? filteredSlides : pptData.slides || []).map((slide, index) => (
                    <ListItem 
                      key={index}
                      button
                      selected={currentSlide === index}
                      onClick={() => goToSlide(index)}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: primaryColor }}>
                          {index + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={slide.title || `Slide ${index + 1}`}
                        secondary={slide.content ? slide.content.substring(0, 50) + '...' : 'No content'}
                      />
                    </ListItem>
                  ))}
                </SlidesList>
                
                <InfoPanel>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <InfoIcon sx={{ fontSize: 16, mr: 1 }} />
                    Presentation Info
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Title:</strong> {pptData.title || 'Untitled'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Author:</strong> {pptData.author || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Created:</strong> {formatDate(pptData.created_date)}
                  </Typography>
                </InfoPanel>
              </SlidesPanel>

              {/* Main Content */}
              <MainContent>
                <SlideViewer>
                  {currentSlideData ? (
                    <SlideCard>
                      <SlideContent>
                        <div className="slide-title">
                          {currentSlideData.title || `Slide ${currentSlide + 1}`}
                        </div>
                        <div 
                          className="slide-content"
                          dangerouslySetInnerHTML={{ 
                            __html: currentSlideData.content || 'No content available' 
                          }}
                        />
                      </SlideContent>
                    </SlideCard>
                  ) : (
                    <Typography color="text.secondary">
                      No slide content available
                    </Typography>
                  )}
                </SlideViewer>
                
                <SlideNavigation>
                  <Button
                    startIcon={<NavigateBeforeIcon />}
                    onClick={prevSlide}
                    disabled={currentSlide === 0}
                  >
                    Previous
                  </Button>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">
                      {currentSlide + 1} of {pptData.slides?.length || 0}
                    </Typography>
                    {pptData.slides && pptData.slides.length > 1 && (
                      <Pagination
                        count={pptData.slides.length}
                        page={currentSlide + 1}
                        onChange={(event, value) => goToSlide(value - 1)}
                        size="small"
                        showFirstButton
                        showLastButton
                      />
                    )}
                  </Box>
                  
                  <Button
                    endIcon={<NavigateNextIcon />}
                    onClick={nextSlide}
                    disabled={!pptData.slides || currentSlide === pptData.slides.length - 1}
                  >
                    Next
                  </Button>
                </SlideNavigation>
              </MainContent>
            </>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              padding: 4
            }}>
              <Typography color="text.secondary">
                {t('viewer.no_powerpoint_content')}
              </Typography>
            </Box>
          )}
        </PowerPointContainer>
      </DialogContent>
    </StyledDialog>
  );
};

export default PowerPointViewer;
